/**
 * EligibleInvoicesDetailPanel
 * Panel de detalle para jobs de tipo EligibleInvoicesReport.
 * Recibe result por prop desde JobDetailDrawer — cero re-fetch (eliminado doble fetch).
 * Usa DataGridPro virtualizado con master-detail para lines de cada factura.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  Chip,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  DataGridPro,
  GridToolbarContainer,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import type { GridColDef, GridFilterModel, GridRowParams } from "@mui/x-data-grid-pro";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import {
  CheckCircleOutlined,
  CancelOutlined,
  LinkOffOutlined,
  AssignmentLateOutlined,
  MoneyOffOutlined,
  HelpOutlineOutlined,
  WarningAmberOutlined,
  DownloadOutlined,
  ReceiptLong as GenerateIcon,
  LinkOutlined,
  AssignmentOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { downloadJobResult } from "@/services/jobs-api";
import type {
  EligibleInvoicesResultDto,
  EligibleInvoiceItemDto,
  DiscardedInvoiceItemDto,
  CarrierInvoiceLineDto,
  MappingWarningDto,
} from "@/types/jobs";
import { CarrierInvoiceActionsModal } from "@/components/internal-invoices/CarrierInvoiceActionsModal";
import { BulkGenerateInternalInvoices } from "@/components/internal-invoices/BulkGenerateInternalInvoices";
import {
  getBookingDisplayText,
  getFilteredInvoiceIds,
  getSearchTerms,
  tokenizeSearchInput,
  matchesBooking,
  matchesInvoiceNumber,
  resolveBulkAllAction,
  resolveSelectedIds,
} from "./eligible-invoices-search";
import type { SearchableInvoiceRow } from "./eligible-invoices-search";
import { STAT_NUMBER_SX, SECTION_LABEL_SX, CHIP_GEOMETRIC_SX } from "../jobs-design-tokens";

// ─── Tab indices del modal ────────────────────────────────────────────────────

const TAB_GENERAR = 0;
const TAB_VINCULAR = 1;
const TAB_ASIGNAR = 2;
const TAB_CONCILIACION = 3;

// ─── Mapa de razones de descarte ──────────────────────────────────────────────

interface ReasonMeta {
  label: string;
  color: "error" | "warning" | "default";
  icon: React.ReactNode;
  description: string;
  action: "vincular" | "asignar" | null;
}

function getReasonMeta(reason: string): ReasonMeta {
  const r = reason?.toUpperCase() ?? "";
  if (r.includes("NO CONCILIADO") || r.includes("CONCILIACI"))
    return {
      label: "Sin conciliar",
      color: "error",
      icon: <LinkOffOutlined sx={{ fontSize: 13 }} />,
      description: "El booking no está vinculado con un booking naviera",
      action: "vincular",
    };
  if (r.includes("NO HAY CONTRATO") || r.includes("SIN CONTRATO") || r.includes("CONTRATO"))
    return {
      label: "Sin contrato",
      color: "warning",
      icon: <AssignmentLateOutlined sx={{ fontSize: 13 }} />,
      description: "El booking naviera no tiene línea de contrato asignada",
      action: "asignar",
    };
  if (r.includes("FALTA PRECIO") || r.includes("PRECIO") || r.includes("TARIFA"))
    return {
      label: "Sin tarifa",
      color: "warning",
      icon: <MoneyOffOutlined sx={{ fontSize: 13 }} />,
      description: "El contrato no tiene tarifa configurada para este tipo de equipo",
      action: null,
    };
  if (r.includes("NO ENCONTRADO") || r.includes("NOT FOUND"))
    return {
      label: "No encontrado",
      color: "error",
      icon: <HelpOutlineOutlined sx={{ fontSize: 13 }} />,
      description: "Booking no encontrado en Core",
      action: null,
    };
  return {
    label: reason,
    color: "default",
    icon: <CancelOutlined sx={{ fontSize: 13 }} />,
    description: reason,
    action: null,
  };
}

// ─── Estado del modal de acciones ────────────────────────────────────────────

interface ModalState {
  open: boolean;
  carrierInvoiceId: number;
  bookingId: number;
  bookingReference: string | null;
  linkedBookingId: number | null;
  initialTab: number;
}

const MODAL_CLOSED: ModalState = {
  open: false,
  carrierInvoiceId: 0,
  bookingId: 0,
  bookingReference: null,
  linkedBookingId: null,
  initialTab: TAB_GENERAR,
};

// ─── Tabla de líneas de contrato (sub-panel master-detail) ────────────────────

function LinesTable({ lines }: { lines: CarrierInvoiceLineDto[] }) {
  const fmt = (v: unknown): string => {
    if (v == null) return "—";
    if (typeof v === "number") return isNaN(v) ? "—" : v.toLocaleString("es", { minimumFractionDigits: 2 });
    if (typeof v === "object") {
      const o = v as Record<string, unknown>;
      const pv = o.parsedValue ?? o.ParsedValue;
      if (pv !== undefined && pv !== null) {
        const n = Number(pv);
        return isNaN(n) ? "—" : n.toLocaleString("es", { minimumFractionDigits: 2 });
      }
      const src = (o.source ?? o.Source) as string | undefined;
      if (src) {
        const n = parseFloat(src);
        return isNaN(n) ? "—" : n.toLocaleString("es", { minimumFractionDigits: 2 });
      }
    }
    return "—";
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = (l: any, pascal: string, camel: string) => l[pascal] ?? l[camel];

  return (
    <Box sx={{ p: 1.5, bgcolor: "rgba(5,150,105,0.03)" }}>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.68rem",
          "& th, & td": { px: 1, py: 0.5, textAlign: "left", whiteSpace: "nowrap" },
          "& th": {
            color: "text.secondary",
            fontWeight: 600,
            fontSize: "0.62rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            bgcolor: "rgba(0,0,0,0.04)",
            borderBottom: "1px solid",
            borderColor: "divider",
          },
          "& tr:not(:last-child) td": { borderBottom: "1px solid", borderColor: "divider" },
          "& tbody tr:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.02)" },
        }}
      >
        <thead>
          <tr>
            <th>Código</th>
            <th>Descripción</th>
            <th style={{ textAlign: "right" }}>Cant.</th>
            <th style={{ textAlign: "right" }}>P.Unit</th>
            <th style={{ textAlign: "right" }}>Importe</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={g(l, "CarrierInvoiceLineId", "carrierInvoiceLineId")}>
              <td><Chip label={g(l, "ConceptCode", "conceptCode") ?? "—"} size="small" sx={{ height: 16, fontSize: "0.62rem", fontFamily: "monospace", ...CHIP_GEOMETRIC_SX }} /></td>
              <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{g(l, "ConceptDescription", "conceptDescription")}</td>
              <td style={{ textAlign: "right" }}>{fmt(g(l, "Quantity", "quantity"))}</td>
              <td style={{ textAlign: "right" }}>{fmt(g(l, "UnitPrice", "unitPrice"))}</td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(g(l, "LineAmount", "lineAmount"))}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

// ─── Tabla de warnings de mapeo (sub-panel master-detail) ────────────────────

export function MappingWarningsTable({ warnings }: { warnings: MappingWarningDto[] }) {
  // Soporta tanto PascalCase (legacy blob) como camelCase
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = (w: any, pascal: string, camel: string) => w[pascal] ?? w[camel];

  return (
    <Box sx={{ p: 1.5, bgcolor: "rgba(245, 158, 11, 0.06)", borderTop: "1px solid", borderColor: "warning.light" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
        <WarningAmberOutlined sx={{ fontSize: 16, color: "warning.main" }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: "warning.dark", textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.68rem" }}>
          Conceptos sin mapeo a contrato ({warnings.length})
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontSize: "0.68rem" }}>
        Estas líneas se omitirán al generar la factura interna. Configurar el mapeo en el catálogo de Conceptos de Factura.
      </Typography>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.68rem",
          "& th, & td": { px: 1, py: 0.5, textAlign: "left", whiteSpace: "nowrap" },
          "& th": {
            color: "text.secondary",
            fontWeight: 600,
            fontSize: "0.62rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            bgcolor: "rgba(245, 158, 11, 0.1)",
            borderBottom: "1px solid",
            borderColor: "warning.light",
          },
          "& tr:not(:last-child) td": { borderBottom: "1px solid", borderColor: "divider" },
        }}
      >
        <thead>
          <tr>
            <th>Código</th>
            <th>Descripción</th>
            <th>Clasificación</th>
            <th style={{ textAlign: "right" }}>Línea</th>
          </tr>
        </thead>
        <tbody>
          {warnings.map((w) => (
            <tr key={g(w, "CarrierInvoiceLineId", "carrierInvoiceLineId")}>
              <td>
                <Chip
                  label={g(w, "ConceptCode", "conceptCode") ?? "—"}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 16, fontSize: "0.62rem", fontFamily: "monospace", ...CHIP_GEOMETRIC_SX }}
                />
              </td>
              <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>
                {g(w, "ConceptDescription", "conceptDescription")}
              </td>
              <td>
                <Chip
                  label={g(w, "Classification", "classification") ?? "—"}
                  size="small"
                  variant="outlined"
                  sx={{ height: 16, fontSize: "0.62rem", ...CHIP_GEOMETRIC_SX }}
                />
              </td>
              <td style={{ textAlign: "right", fontFamily: "monospace", color: "text.secondary" }}>
                #{g(w, "CarrierInvoiceLineId", "carrierInvoiceLineId")}
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

// ─── Resumen de razones ───────────────────────────────────────────────────────

function DiscardSummary({ discarded }: { discarded: DiscardedInvoiceItemDto[] }) {
  const counts: Record<string, number> = {};
  for (const inv of discarded) {
    const meta = getReasonMeta(inv.Reason);
    counts[meta.label] = (counts[meta.label] ?? 0) + 1;
  }

  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
      {Object.entries(counts).map(([label, count]) => {
        const meta = getReasonMeta(label);
        return (
          <Chip
            key={label}
            icon={meta.icon as React.ReactElement}
            label={`${label}: ${count}`}
            size="small"
            color={meta.color}
            variant="outlined"
            sx={{ height: 22, fontSize: "0.7rem", ...CHIP_GEOMETRIC_SX }}
          />
        );
      })}
    </Box>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Box
      sx={{
        flex: 1,
        textAlign: "center",
        py: 1.5,
        px: 0.5,
        borderRadius: 2,
        bgcolor: `${color}0a`,
        border: "1px solid",
        borderColor: `${color}20`,
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: `0 2px 8px ${color}15` },
      }}
    >
      <Typography sx={{ ...STAT_NUMBER_SX, color, fontSize: "24px" }}>{value}</Typography>
      <Typography sx={{ ...SECTION_LABEL_SX, lineHeight: 1.2, display: "block", mt: 0.25 }}>{label}</Typography>
    </Box>
  );
}

// ─── Quick filter toolbar ─────────────────────────────────────────────────────

// GridToolbarQuickFilter (v8) se muestra colapsado tras un icono; el buscador debe verse siempre.
function QuickFilterToolbar() {
  const apiRef = useGridApiContext();
  const [text, setText] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleChange = (value: string) => {
    setText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      apiRef.current.setQuickFilterValues(tokenizeSearchInput(value));
    }, 200);
  };

  return (
    <GridToolbarContainer sx={{ px: 1, py: 0.5 }}>
      <TextField
        type="search"
        variant="outlined"
        size="small"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Buscar por nº de factura o booking"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ width: 300, "& .MuiOutlinedInput-root": { fontSize: "0.8rem", height: 30, borderRadius: "6px" } }}
      />
    </GridToolbarContainer>
  );
}

// ─── Búsqueda: solo N° factura y booking participan ──────────────────────────

const toQuickFilterFn =
  (matcher: (row: SearchableInvoiceRow, term: string) => boolean): GridColDef["getApplyQuickFilterFn"] =>
  (term) =>
  (_value, row) =>
    matcher(row, String(term));

const NOT_SEARCHABLE: Pick<GridColDef, "getApplyQuickFilterFn"> = { getApplyQuickFilterFn: () => null };

const INITIAL_FILTER_MODEL: GridFilterModel = {
  items: [],
  quickFilterValues: [],
  quickFilterExcludeHiddenColumns: false,
};

// ─── Panel principal ──────────────────────────────────────────────────────────

interface Props {
  result: EligibleInvoicesResultDto;
  jobId: number;
  service: "core" | "finance";
}

type EligibleRow = EligibleInvoiceItemDto & { id: number };
type DiscardedRow = DiscardedInvoiceItemDto & { id: number };
type JobResultRow = Record<string, unknown>;

function getJobField<T>(row: JobResultRow, pascal: string, camel: string): T | undefined {
  return (row[pascal] ?? row[camel]) as T | undefined;
}

function toNumberOrFallback(value: unknown, fallback: number): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

export function normalizeEligibleInvoiceRow(item: unknown): EligibleRow {
  const row = item as JobResultRow;
  const lines = getJobField<CarrierInvoiceLineDto[] | null>(row, "Lines", "lines") ?? [];
  const mappingWarnings = getJobField<MappingWarningDto[] | null>(row, "MappingWarnings", "mappingWarnings") ?? [];
  const carrierInvoiceId = toNumberOrFallback(getJobField(row, "CarrierInvoiceId", "carrierInvoiceId"), 0);
  const bookingId = toNumberOrFallback(getJobField(row, "BookingId", "bookingId"), 0);
  const pendingLinesFallback = Array.isArray(lines) ? lines.length : 0;

  return {
    ...(item as EligibleInvoiceItemDto),
    id: carrierInvoiceId,
    CarrierInvoiceId: carrierInvoiceId,
    InvoiceNumber: getJobField<string>(row, "InvoiceNumber", "invoiceNumber") ?? "",
    InvoiceDate: getJobField<string>(row, "InvoiceDate", "invoiceDate") ?? "",
    BookingId: bookingId,
    BookingReference: getJobField<string | null>(row, "BookingReference", "bookingReference") ?? null,
    BookingSource: getJobField<string | null>(row, "BookingSource", "bookingSource") ?? null,
    LinkedBookingId: getJobField<number | null>(row, "LinkedBookingId", "linkedBookingId") ?? null,
    TotalEquipment: getJobField<number | null>(row, "TotalEquipment", "totalEquipment") ?? null,
    CurrencyCode: getJobField<string>(row, "CurrencyCode", "currencyCode") ?? "",
    PendingLines: toNumberOrFallback(getJobField(row, "PendingLines", "pendingLines"), pendingLinesFallback),
    TrafficType: getJobField<string | null>(row, "TrafficType", "trafficType") ?? null,
    ContractLineIds: getJobField<number[] | null>(row, "ContractLineIds", "contractLineIds") ?? null,
    Lines: lines,
    MappingWarnings: mappingWarnings,
  };
}

function normalizeDiscardedInvoiceRow(item: unknown): DiscardedRow {
  const row = item as JobResultRow;
  return {
    ...normalizeEligibleInvoiceRow(item),
    Reason: getJobField<string>(row, "Reason", "reason") ?? "",
  };
}

export function EligibleInvoicesDetailPanel({ result, jobId, service }: Props) {
  const [tab, setTab] = useState<0 | 1>(0);
  const [modal, setModal] = useState<ModalState>(MODAL_CLOSED);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [eligibleFilterModel, setEligibleFilterModel] = useState<GridFilterModel>(INITIAL_FILTER_MODEL);

  const eligible = useMemo(() => result.Eligible ?? [], [result.Eligible]);
  const discarded = useMemo(() => result.Discarded ?? [], [result.Discarded]);
  const eligibleRows: EligibleRow[] = useMemo(() => eligible.map(normalizeEligibleInvoiceRow), [eligible]);
  const discardedRows: DiscardedRow[] = useMemo(
    () => (discarded as DiscardedInvoiceItemDto[]).map(normalizeDiscardedInvoiceRow),
    [discarded],
  );
  const total = result.TotalAnalyzed ?? 0;
  const totalEligible = result.TotalEligible ?? 0;
  const totalDiscarded = result.TotalDiscarded ?? 0;
  const totalLines = result.TotalEligibleLines ?? 0;

  const allEligibleIds = eligibleRows.map((e) => e.CarrierInvoiceId);

  // Las acciones masivas actúan sobre lo que la búsqueda deja a la vista (todas las páginas).
  const terms = getSearchTerms(eligibleFilterModel.quickFilterValues);
  const filteredIds = getFilteredInvoiceIds(eligibleRows, terms);
  const bulkAll = resolveBulkAllAction({
    allIds: allEligibleIds,
    filteredIds,
    searchActive: terms.length > 0,
    hasColumnFilter: eligibleFilterModel.items.length > 0,
  });
  const passingIds = new Set(filteredIds);
  const actionableSelectedIds = terms.length > 0 ? selectedIds.filter((id) => passingIds.has(id)) : selectedIds;
  const hasSelection = actionableSelectedIds.length > 0;
  const bulkAllButton = (
    <Button
      size="small"
      variant="contained"
      color="success"
      disabled={!bulkAll.enabled}
      startIcon={<GenerateIcon sx={{ fontSize: 14 }} />}
      sx={{ textTransform: "none", fontSize: "0.75rem", height: 28 }}
    >
      {bulkAll.label}
    </Button>
  );

  const handleDownload = useCallback(async () => {
    try {
      const blob = await downloadJobResult(jobId, service);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eligible-invoices-${jobId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silencioso
    }
  }, [jobId, service]);

  const openGenerateModal = useCallback((inv: EligibleInvoiceItemDto) => {
    setModal({
      open: true,
      carrierInvoiceId: inv.CarrierInvoiceId,
      bookingId: inv.BookingId,
      bookingReference: inv.BookingReference ?? null,
      linkedBookingId: inv.LinkedBookingId ?? null,
      initialTab: TAB_GENERAR,
    });
  }, []);

  const openActionModal = useCallback((inv: DiscardedInvoiceItemDto, initialTab: number) => {
    setModal({
      open: true,
      carrierInvoiceId: inv.CarrierInvoiceId,
      bookingId: inv.BookingId,
      bookingReference: inv.BookingReference ?? null,
      linkedBookingId: inv.LinkedBookingId ?? null,
      initialTab,
    });
  }, []);

  const closeModal = useCallback(() => setModal(MODAL_CLOSED), []);

  // ─── Column defs ────────────────────────────────────────────────────────────

  const eligibleColumns: GridColDef<EligibleRow>[] = [
    {
      field: "InvoiceNumber",
      headerName: "N° Factura",
      flex: 1,
      getApplyQuickFilterFn: toQuickFilterFn(matchesInvoiceNumber),
      renderCell: ({ value }) => (
        <Typography variant="caption" fontWeight={700} sx={{ fontFamily: "monospace", fontSize: "12px" }}>
          {value}
        </Typography>
      ),
    },
    {
      field: "BookingReference",
      headerName: "Booking",
      flex: 1,
      getApplyQuickFilterFn: toQuickFilterFn(matchesBooking),
      renderCell: ({ row }) => (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: "11px" }}>
          {getBookingDisplayText(row)}
        </Typography>
      ),
    },
    {
      field: "InvoiceDate",
      ...NOT_SEARCHABLE,
      headerName: "Fecha",
      width: 110,
      renderCell: ({ value }) =>
        value ? (
          <Typography variant="caption" color="text.secondary">
            {new Date(value as string).toLocaleDateString("es")}
          </Typography>
        ) : null,
    },
    {
      field: "PendingLines",
      ...NOT_SEARCHABLE,
      headerName: "Líneas",
      width: 80,
      renderCell: ({ value }) => (
        <Chip
          label={`${value}`}
          size="small"
          variant="outlined"
          color="success"
          sx={{ height: 16, fontSize: "0.62rem", ...CHIP_GEOMETRIC_SX }}
        />
      ),
    },
    {
      field: "CurrencyCode",
      ...NOT_SEARCHABLE,
      headerName: "Moneda",
      width: 80,
      renderCell: ({ value }) =>
        value ? (
          <Chip label={value} size="small" variant="outlined" sx={{ height: 16, fontSize: "0.62rem", ...CHIP_GEOMETRIC_SX }} />
        ) : null,
    },
    {
      field: "MappingWarnings",
      ...NOT_SEARCHABLE,
      headerName: "Avisos",
      width: 90,
      sortable: false,
      renderCell: ({ row }) => {
        const count = row.MappingWarnings?.length ?? 0;
        if (count === 0) return null;
        return (
          <Tooltip title={`${count} concepto(s) sin mapeo a contrato — expandir fila para ver detalle`} placement="top-start">
            <Chip
              icon={<WarningAmberOutlined sx={{ fontSize: 13 }} />}
              label={`${count} sin mapeo`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem", cursor: "help", ...CHIP_GEOMETRIC_SX }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      ...NOT_SEARCHABLE,
      type: "actions",
      width: 60,
      getActions: ({ row }: GridRowParams<EligibleRow>) => [
        <GridActionsCellItem
          key="generate"
          icon={<Tooltip title="Generar factura interna"><GenerateIcon sx={{ fontSize: 16 }} /></Tooltip>}
          label="Generar factura interna"
          onClick={() => openGenerateModal(row)}
        />,
      ],
    },
  ];

  const discardedColumns: GridColDef<DiscardedRow>[] = [
    {
      field: "InvoiceNumber",
      headerName: "N° Factura",
      flex: 1,
      getApplyQuickFilterFn: toQuickFilterFn(matchesInvoiceNumber),
      renderCell: ({ value }) => (
        <Typography variant="caption" fontWeight={700} sx={{ fontFamily: "monospace", fontSize: "12px" }}>
          {value}
        </Typography>
      ),
    },
    {
      field: "BookingReference",
      headerName: "Booking",
      flex: 1,
      getApplyQuickFilterFn: toQuickFilterFn(matchesBooking),
      renderCell: ({ row }) => (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: "11px" }}>
          {getBookingDisplayText(row)}
        </Typography>
      ),
    },
    {
      field: "InvoiceDate",
      ...NOT_SEARCHABLE,
      headerName: "Fecha",
      width: 110,
      renderCell: ({ value }) =>
        value ? (
          <Typography variant="caption" color="text.secondary">
            {new Date(value as string).toLocaleDateString("es")}
          </Typography>
        ) : null,
    },
    {
      field: "Reason",
      ...NOT_SEARCHABLE,
      headerName: "Motivo",
      flex: 1.2,
      renderCell: ({ value }) => {
        if (!value) return null;
        const meta = getReasonMeta(value as string);
        return (
          <Tooltip title={meta.description} placement="top-start">
            <Chip
              icon={meta.icon as React.ReactElement}
              label={meta.label}
              size="small"
              color={meta.color}
              sx={{ height: 20, fontSize: "0.68rem", cursor: "help", ...CHIP_GEOMETRIC_SX }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      ...NOT_SEARCHABLE,
      type: "actions",
      width: 80,
      getActions: ({ row }: GridRowParams<DiscardedRow>) => {
        const meta = getReasonMeta(row.Reason);
        const actions = [];
        if (meta.action === "vincular") {
          const initialTab = row.LinkedBookingId ? TAB_CONCILIACION : TAB_VINCULAR;
          actions.push(
            <GridActionsCellItem
              key="vincular"
              icon={<Tooltip title={row.LinkedBookingId ? "Ver conciliación" : "Vincular booking naviera"}><LinkOutlined sx={{ fontSize: 14 }} /></Tooltip>}
              label="Vincular"
              onClick={() => openActionModal(row, initialTab)}
            />
          );
        }
        if (meta.action === "asignar") {
          actions.push(
            <GridActionsCellItem
              key="asignar"
              icon={<Tooltip title="Asignar línea de contrato"><AssignmentOutlined sx={{ fontSize: 14 }} /></Tooltip>}
              label="Asignar"
              onClick={() => openActionModal(row, TAB_ASIGNAR)}
            />
          );
        }
        return actions;
      },
    },
  ];

  // ─── Master-detail for eligible lines ───────────────────────────────────────

  function getEligibleDetailContent({ row }: GridRowParams<EligibleRow>) {
    const lines = row.Lines;
    const warnings = row.MappingWarnings ?? [];
    if (!lines?.length && warnings.length === 0) return null;
    return (
      <Box>
        {lines?.length ? <LinesTable lines={lines} /> : null}
        {warnings.length > 0 ? <MappingWarningsTable warnings={warnings} /> : null}
      </Box>
    );
  }

  function getDetailPanelHeight() {
    return "auto" as const;
  }

  // ─── Rows ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Estadísticas */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 1.5, flexShrink: 0 }}>
        <StatCard label="Analizadas" value={total} color="#0891b2" />
        <StatCard label="Elegibles" value={totalEligible} color="#059669" />
        <StatCard label="Líneas" value={totalLines} color="#7c3aed" />
        <StatCard label="Descartadas" value={totalDiscarded} color="#dc2626" />
      </Box>

      {result.GeneratedBy && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, flexShrink: 0 }}>
          {result.GeneratedBy} · {new Date(result.GeneratedAt).toLocaleString("es")}
        </Typography>
      )}

      <Divider sx={{ mb: 1.5, flexShrink: 0 }} />

      {/* Resumen de causas de descarte */}
      {discarded.length > 0 && (
        <Box sx={{ flexShrink: 0 }}>
          <DiscardSummary discarded={discarded as DiscardedInvoiceItemDto[]} />
        </Box>
      )}

      {/* Tabs + DataGridPro */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              setSelectedIds([]);
              setEligibleFilterModel(INITIAL_FILTER_MODEL);
            }}
            sx={{ minHeight: 34 }}
            TabIndicatorProps={{ style: { height: 2 } }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CheckCircleOutlined sx={{ fontSize: 14, color: "#059669" }} />
                  Elegibles
                  <Chip label={eligible.length} size="small" color="success" sx={{ height: 18, fontSize: "0.68rem", ...CHIP_GEOMETRIC_SX }} />
                </Box>
              }
              sx={{ minHeight: 34, textTransform: "none", fontSize: "0.8rem", py: 0 }}
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CancelOutlined sx={{ fontSize: 14, color: "#dc2626" }} />
                  Descartadas
                  <Chip label={discarded.length} size="small" color="error" sx={{ height: 18, fontSize: "0.68rem", ...CHIP_GEOMETRIC_SX }} />
                </Box>
              }
              sx={{ minHeight: 34, textTransform: "none", fontSize: "0.8rem", py: 0 }}
            />
          </Tabs>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* Botones generar — solo en tab elegibles */}
            {tab === 0 && eligible.length > 0 && (
              <>
                {hasSelection && (
                  <BulkGenerateInternalInvoices
                    selectedIds={actionableSelectedIds}
                    onSuccess={() => setSelectedIds([])}
                    trigger={
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<GenerateIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: "none", fontSize: "0.75rem", height: 28 }}
                      >
                        Generar sel. ({actionableSelectedIds.length})
                      </Button>
                    }
                  />
                )}
                {bulkAll.enabled ? (
                  <BulkGenerateInternalInvoices selectedIds={bulkAll.ids} trigger={bulkAllButton} />
                ) : (
                  // Fuera del wrapper de generación masiva: este abre el diálogo con cualquier clic.
                  <Tooltip title={bulkAll.disabledReason ?? ""}>
                    <span>{bulkAllButton}</span>
                  </Tooltip>
                )}
              </>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadOutlined sx={{ fontSize: 14 }} />}
              sx={{ textTransform: "none", height: 28, fontSize: "0.75rem" }}
              onClick={handleDownload}
            >
              Descargar JSON
            </Button>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, mt: 1 }}>
          {tab === 0 ? (
            <DataGridPro
              key="eligible-grid"
              rows={eligibleRows}
              columns={eligibleColumns as GridColDef[]}
              density="compact"
              checkboxSelection
              disableRowSelectionOnClick
              filterModel={eligibleFilterModel}
              onFilterModelChange={(model) =>
                setEligibleFilterModel({ ...model, quickFilterExcludeHiddenColumns: false })
              }
              onRowSelectionModelChange={(model) => setSelectedIds(resolveSelectedIds(model, allEligibleIds))}
              getDetailPanelContent={getEligibleDetailContent}
              getDetailPanelHeight={getDetailPanelHeight}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              pagination
              disableColumnMenu={false}
              showToolbar
              slots={{ toolbar: QuickFilterToolbar }}
              sx={{
                border: 0,
                height: "100%",
                "& .MuiDataGrid-row:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.015)" },
                borderRadius: "8px",
                overflow: "hidden",
              }}
              localeText={{
                noRowsLabel: "No hay facturas elegibles",
                noResultsOverlayLabel: "No hay facturas elegibles",
              }}
            />
          ) : (
            <DataGridPro
              key="discarded-grid"
              rows={discardedRows}
              columns={discardedColumns as GridColDef[]}
              density="compact"
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
                filter: { filterModel: INITIAL_FILTER_MODEL },
              }}
              pagination
              disableColumnMenu={false}
              showToolbar
              slots={{ toolbar: QuickFilterToolbar }}
              sx={{
                border: 0,
                height: "100%",
                "& .MuiDataGrid-row:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.015)" },
                borderRadius: "8px",
                overflow: "hidden",
              }}
              localeText={{
                noRowsLabel: "No hay facturas descartadas",
                noResultsOverlayLabel: "No hay facturas descartadas",
              }}
            />
          )}
        </Box>
      </Box>

      {/* Modal de acciones (generar / vincular / asignar) */}
      {modal.open && (
        <CarrierInvoiceActionsModal
          open={modal.open}
          onClose={closeModal}
          carrierInvoiceId={modal.carrierInvoiceId}
          bookingId={modal.bookingId}
          linkedBookingId={modal.linkedBookingId}
          bookingReference={modal.bookingReference}
          linkedBookingReference={null}
          operationType={null}
          contractSq={null}
          initialTab={modal.initialTab}
        />
      )}
    </Box>
  );
}
